/** @file Bridge between editor AppState and PlayDoc. */
import { playDocSchema, PLAY_SCHEMA_VERSION } from "../persistence/schemas.js";
import { cloneScene, createInitialScene, projectSceneToTokens } from "./play.js";

/** @typedef {import("../types/schemas.js").PlayDoc} PlayDoc */
/** @typedef {import("../types/schemas.js").Scene} Scene */
/** @typedef {import("./state.js").AppState} AppState */

/**
 * @param {{ name: string, teamId?: string | null, thumbnail?: string }} meta
 * @param {AppState} state
 * @returns {Omit<PlayDoc, "id" | "createdAt" | "updatedAt">}
 */
export function stateToPlay(meta, state) {
  const scenes =
    state.scenes && state.scenes.length > 0
      ? state.scenes.map(cloneScene)
      : [createInitialScene(state.tokens)];

  /** @type {Omit<PlayDoc, "id" | "createdAt" | "updatedAt">} */
  const out = {
    name: meta.name,
    tokens: state.tokens.map((t) => ({ ...t })),
    drawings: state.drawings.map((d) => ({
      ...d,
      points: d.points.map((p) => ({ ...p })),
    })),
    scenes,
    schemaVersion: PLAY_SCHEMA_VERSION,
    ...(meta.thumbnail !== undefined ? { thumbnail: meta.thumbnail } : {}),
    ...(meta.teamId !== undefined ? { teamId: meta.teamId } : {}),
  };
  // Validate via schema (full doc shape) — synthesize dummy id/dates.
  const now = new Date();
  playDocSchema.parse({ id: "_validate", createdAt: now, updatedAt: now, ...out });
  return out;
}

/**
 * @param {PlayDoc} doc
 * @returns {Pick<AppState, "tokens" | "drawings" | "scenes" | "activeSceneIndex">}
 */
export function playToState(doc) {
  const scenes = doc.scenes.map(cloneScene);
  const baseTokens = doc.tokens.map((t) => ({ ...t }));
  const firstScene = scenes[0];
  const tokens = firstScene ? projectSceneToTokens(firstScene, baseTokens) : baseTokens;
  return {
    tokens,
    drawings: doc.drawings.map((d) => ({
      ...d,
      points: d.points.map((p) => ({ ...p })),
    })),
    scenes,
    activeSceneIndex: 0,
  };
}
